import './brand';
import './auth';
import './community';
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Post from 'flarum/common/models/Post';
import Discussion from 'flarum/common/models/Discussion';
import Button from 'flarum/common/components/Button';
import LinkButton from 'flarum/common/components/LinkButton';
import Dropdown from 'flarum/common/components/Dropdown';
import Modal from 'flarum/common/components/Modal';
import CommentPost from 'flarum/forum/components/CommentPost';
import Comment from 'flarum/forum/components/Comment';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import UserCard from 'flarum/forum/components/UserCard';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import TextEditor from 'flarum/common/components/TextEditor';
import extractText from 'flarum/common/utils/extractText';
import Tag from 'ext:flarum/tags/common/models/Tag';
import { track } from './analytics';
const originals = new Set();
const visible = new Map();
let language = 'en';
let polls = 0;
let paused = false;
const label = (key, values) => app.translator.trans('bydtalk.forum.' + key, values);
const showModal = (component, attrs) => {
  app.drawer.hide();
  return app.modal.show(component, attrs);
};
const endpoint = path => app.forum.attribute('apiUrl') + '/bydtalk/' + path;
const names = () => app.forum.attribute('btLanguages') || {
  en: 'English'
};
const body = post => originals.has(post.id()) ? post.attribute('btTranslation')?.originalHtml : post.attribute('contentHtml');
const originalLanguage = t => t?.sourceLanguage || 'und';
const groupText = group => {
  const names = app.forum.attribute('btVehicleLabels') || {};
  const model = names.models?.[group.model];
  return [model?.[group.market] || model?.default || group.model, names.markets?.[group.market] || group.market, group.year, names.powertrains?.[group.powertrain] || group.powertrain, group.version, group.test].filter(Boolean).join(' · ');
};
async function saveLanguages(list, navigate) {
  if (navigate === void 0) {
    navigate = true;
  }
  const ordered = [...new Set(list)].filter(code => names()[code]);
  if (!ordered.length) return;
  if (app.session.user) await app.session.user.savePreferences({
    btLanguages: ordered
  });
  document.cookie = `bt_languages=${encodeURIComponent(JSON.stringify(ordered))}; path=/; max-age=31536000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
  if (ordered[0] !== language) track('language_change', {
    from_language: language,
    to_language: ordered[0]
  });
  if (navigate) location.href = location.pathname.replace(/^\/l\/[a-z]{2,3}(?:-[A-Za-z]{2,8})?\/d\//, `/l/${ordered[0]}/d/`) + location.search;
}
class LanguagesModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    let cookie = [];
    try {
      cookie = JSON.parse(decodeURIComponent(document.cookie.match(/(?:^|; )bt_languages=([^;]*)/)?.[1] || '[]'));
    } catch {}
    this.languages = [...new Set(app.session.user?.preferences().btLanguages || (cookie.length ? cookie : [language]))].filter(code => names()[code]);
    if (!this.languages.length) this.languages = [language];
  }
  className() {
    return 'Modal--small';
  }
  title() {
    return label('reading_language');
  }
  content() {
    return m("form", {
      className: "Modal-body",
      onsubmit: async e => {
        e.preventDefault();
        this.loading = true;
        try {
          await saveLanguages(this.languages);
        } catch {
          this.alertAttrs = {
            type: 'error',
            content: label('save_error')
          };
          this.loading = false;
          m.redraw();
        }
      }
    }, m("p", null, label('languages_help')), this.languages.map((code, i) => m("div", {
      className: "BydTalkRow"
    }, m("label", {
      className: "BydTalkLanguageField"
    }, m("span", null, label(i === 0 ? 'default_language' : 'familiar_language')), m("select", {
      className: "FormControl",
      disabled: this.loading,
      value: code,
      onchange: e => this.languages[i] = e.target.value
    }, Object.entries(names()).map(_ref => {
      let [id, name] = _ref;
      return m("option", {
        value: id,
        disabled: id !== code && this.languages.includes(id)
      }, name);
    }))), i > 0 && m(Button, {
      className: "Button Button--icon",
      type: "button",
      disabled: this.loading,
      icon: "fas fa-arrow-up",
      "aria-label": label('move_up'),
      title: label('move_up'),
      onclick: () => {
        [this.languages[i - 1], this.languages[i]] = [this.languages[i], this.languages[i - 1]];
      }
    }), this.languages.length > 1 && m(Button, {
      className: "Button Button--icon",
      type: "button",
      disabled: this.loading,
      icon: "fas fa-times",
      "aria-label": label('remove'),
      title: label('remove'),
      onclick: () => this.languages.splice(i, 1)
    }))), m("div", {
      className: "BydTalkActions"
    }, m(Button, {
      className: "Button",
      type: "button",
      disabled: this.loading || Object.keys(names()).every(code => this.languages.includes(code)),
      onclick: () => {
        const next = Object.keys(names()).find(code => !this.languages.includes(code));
        if (next) this.languages.push(next);
      }
    }, label('add_language')), m(Button, {
      type: "submit",
      className: "Button Button--primary",
      loading: this.loading
    }, label('save'))));
  }
}
class ReportModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.text = '';
  }
  className() {
    return 'Modal--small';
  }
  title() {
    return label('report_title');
  }
  content() {
    return m("form", {
      className: "Modal-body",
      onsubmit: async e => {
        e.preventDefault();
        this.loading = true;
        try {
          await app.request({
            method: 'POST',
            url: endpoint(`translations/${this.attrs.translation.id}/report`),
            body: {
              reason: this.text,
              generation: this.attrs.translation.generation
            }
          });
          this.hide();
          app.alerts.show({
            type: 'success'
          }, label('report_received'));
        } catch {
          this.alertAttrs = {
            type: 'error',
            content: label('save_error')
          };
          this.loading = false;
          m.redraw();
        }
      }
    }, m("p", null, label('report_help')), m("textarea", {
      className: "FormControl",
      "aria-label": label('report_help'),
      required: true,
      minlength: "3",
      maxlength: "2000",
      disabled: this.loading,
      value: this.text,
      oninput: e => this.text = e.target.value
    }), m("div", {
      className: "BydTalkActions"
    }, m(Button, {
      type: "submit",
      className: "Button Button--primary",
      loading: this.loading
    }, label('submit'))));
  }
}
class TextOptions {
  view(_ref2) {
    let {
      attrs: {
        translation: t,
        entryKey: key
      }
    } = _ref2;
    if (!t || t.status === 'original' || t.sourceLanguage === language) return null;
    const original = originals.has(key);
    const sourceName = t.sourceLanguage ? new Intl.DisplayNames([language], {
      type: 'language'
    }).of(t.sourceLanguage) : label('unknown_language');
    return m("div", {
      className: "BydTalkTextOptions"
    }, t.status === 'ready' ? m('[', null, m("span", null, label(original ? 'original_from' : 'translated_from', {
      language: sourceName
    })), m(Button, {
      className: "Button Button--link",
      onclick: () => {
        original ? originals.delete(key) : originals.add(key);
        m.redraw();
      }
    }, label(original ? 'view_translation' : 'view_original'))) : m("span", {
      role: "status"
    }, label(t.status === 'failed' ? 'failed' : paused ? 'paused' : 'pending', {
      language: sourceName
    })), app.session.user && (t.status === 'ready' || app.session.user.isAdmin()) && m(Dropdown, {
      buttonClassName: "Button Button--link Button--icon",
      icon: "fas fa-ellipsis-h",
      label: label('text_options')
    }, t.status === 'ready' && m(Button, {
      onclick: () => showModal(ReportModal, {
        translation: t
      })
    }, label('report_error')), app.session.user.isAdmin() && m(Button, {
      loading: this.retrying,
      disabled: this.retrying,
      onclick: async () => {
        this.retrying = true;
        try {
          await app.request({
            method: 'POST',
            url: endpoint(`translations/${t.id}/retry`)
          });
          location.reload();
        } catch {
          this.retrying = false;
          app.alerts.show({
            type: 'error'
          }, label('save_error'));
          m.redraw();
        }
      }
    }, label('retry'))));
  }
}
class VehiclesModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.nextKey = 0;
    this.groups = (this.attrs.conditions || app.session.user?.attribute('btVehicles') || []).map(group => this.newGroup(group));
    this.loadCatalog();
  }
  newGroup(group) {
    if (group === void 0) {
      group = {};
    }
    return {
      ...group,
      _key: this.nextKey++
    };
  }
  className() {
    return 'BydTalkVehicleModal';
  }
  title() {
    return label(this.attrs.onSave ? 'conditions_title' : 'vehicle_title');
  }
  async loadCatalog() {
    this.fetching = true;
    this.loadError = false;
    try {
      this.catalog = await app.request({
        method: 'GET',
        url: endpoint('vehicles')
      });
      for (const group of this.groups) {
        for (const [key, list] of [['market', 'markets'], ['powertrain', 'powertrains']]) {
          const value = group[key]?.trim().toLowerCase();
          if (!value) continue;
          const item = this.catalog[list].find(item => [item.id, ...item.aliases].some(alias => alias.toLowerCase() === value));
          group[key] = item?.id || '';
          if (!item) this.notice = label('selection_review');
        }
        if (group.model && !group.market) group.market = '_unknown';
        this.reconcile(group);
        if (group.year && !this.catalog.years.includes(group.year)) {
          group.year = '';
          this.notice = label('selection_review');
        }
      }
    } catch {
      this.loadError = true;
    } finally {
      this.fetching = false;
      m.redraw();
    }
  }
  models(group) {
    return group.market === '_unknown' ? this.catalog.models : this.catalog.models.filter(model => model.offerings[group.market]);
  }
  model(group) {
    return this.models(group).find(model => model.id === group.model);
  }
  powertrains(group) {
    const model = this.model(group);
    return model ? model.offerings[group.market]?.powertrains || [...new Set(Object.values(model.offerings).flatMap(offering => offering.powertrains))] : [];
  }
  reconcile(group) {
    if (group.model && !this.model(group)) {
      for (const key of ['model', 'year', 'powertrain', 'version', 'test']) delete group[key];
      this.notice = label('selection_reset');
    } else if (group.powertrain && !this.powertrains(group).includes(group.powertrain)) {
      group.powertrain = '';
      this.notice = label('selection_reset');
    }
  }
  selectMarket(group, value) {
    group.market = value;
    this.notice = null;
    this.reconcile(group);
  }
  selectModel(group, value) {
    if (value === group.model) return;
    if (['year', 'powertrain', 'version', 'test'].some(key => group[key])) this.notice = label('selection_reset');
    for (const key of ['year', 'powertrain', 'version', 'test']) delete group[key];
    group.model = value;
    const options = this.powertrains(group);
    if (options.length === 1) group.powertrain = options[0];
  }
  dataGroups() {
    return this.groups.map(group => Object.fromEntries(['model', 'market', 'year', 'powertrain', ...(this.attrs.onSave ? ['version', 'test'] : [])].filter(key => group[key] && group[key] !== '_unknown').map(key => [key, group[key]])));
  }
  async save(event) {
    event.preventDefault();
    this.loading = true;
    try {
      const groups = this.dataGroups();
      if (this.attrs.onSave) {
        this.attrs.onSave(groups);
        this.hide();
      } else {
        await app.request({
          method: 'POST',
          url: endpoint('vehicles'),
          body: {
            vehicles: groups
          }
        });
        await app.store.find('users', app.session.user.id());
        this.hide();
        app.alerts.show({
          type: 'success'
        }, label('vehicles_saved'));
      }
    } catch {
      this.alertAttrs = {
        type: 'error',
        content: label('save_error')
      };
      this.loading = false;
      m.redraw();
    }
  }
  content() {
    const conditions = !!this.attrs.onSave;
    return m("form", {
      className: "Modal-body",
      onsubmit: event => this.save(event)
    }, m("p", {
      className: "BydTalkVehicleHelp"
    }, label(conditions ? 'conditions_help' : 'vehicle_help')), this.fetching ? m(LoadingIndicator, null) : this.loadError ? m("div", {
      role: "alert"
    }, m("p", null, label('load_error')), m(Button, {
      className: "Button",
      type: "button",
      onclick: () => this.loadCatalog()
    }, label('try_again'))) : m('[', null, conditions && (app.session.user?.attribute('btVehicles') || []).length > 0 && m("div", {
      className: "BydTalkVehiclePresets"
    }, m("p", null, label('use_my_vehicle')), app.session.user.attribute('btVehicles').map(vehicle => m(Button, {
      className: "Button",
      type: "button",
      disabled: this.loading || this.groups.length >= 10,
      onclick: () => {
        const group = this.newGroup(Object.fromEntries(['model', 'market', 'year', 'powertrain'].filter(key => vehicle[key]).map(key => [key, vehicle[key]])));
        if (!group.market) group.market = '_unknown';
        this.groups.push(group);
        this.reconcile(group);
      }
    }, vehicle.label))), !this.groups.length && m("p", {
      className: "BydTalkEmpty"
    }, label(conditions ? 'no_conditions' : 'no_vehicles')), this.notice && m("p", {
      className: "BydTalkSelectionNotice",
      role: "status"
    }, this.notice), this.groups.map((group, index) => m("fieldset", {
      className: "BydTalkVehicleCard",
      key: group._key,
      disabled: this.loading
    }, m("legend", {
      className: "sr-only"
    }, label('vehicle_number', {
      number: index + 1
    })), m("div", {
      className: "BydTalkVehicleCardHeader"
    }, m("div", null, m("h4", null, label('vehicle_number', {
      number: index + 1
    })), !conditions && index === 0 && m("span", {
      className: "BydTalkPrimary"
    }, label('primary_vehicle'))), m(Button, {
      className: "Button Button--link BydTalkRemoveVehicle",
      icon: "fas fa-trash-alt",
      type: "button",
      "aria-label": label('remove_vehicle', {
        number: index + 1
      }),
      onclick: () => {
        this.groups.splice(index, 1);
        this.notice = label('removal_pending');
      }
    }, label('remove'))), m("div", {
      className: "BydTalkFields"
    }, m("label", null, m("span", null, label('market')), m("select", {
      className: "FormControl",
      name: "market",
      required: true,
      value: group.market || '',
      onchange: e => this.selectMarket(group, e.target.value)
    }, m("option", {
      value: "",
      disabled: true
    }, label('choose_market')), this.catalog.markets.map(market => m("option", {
      value: market.id
    }, market.name)), m("option", {
      value: "_unknown"
    }, label('unknown_market')))), m("label", null, m("span", {
      key: "label"
    }, label('model')), m("select", {
      className: "FormControl",
      name: "model",
      key: group.market || '',
      required: true,
      disabled: !group.market,
      value: group.model || '',
      onchange: e => this.selectModel(group, e.target.value)
    }, m("option", {
      value: "",
      disabled: true
    }, label(group.market ? 'not_specified' : 'choose_market_first')), this.models(group).map(model => m("option", {
      value: model.id
    }, model.offerings[group.market]?.name || model.name)))), m("label", null, m("span", null, label('year')), m("select", {
      className: "FormControl",
      name: "year",
      disabled: !group.model,
      value: group.year || '',
      onchange: e => group.year = e.target.value
    }, m("option", {
      value: ""
    }, label('optional_unknown')), this.catalog.years.map(year => m("option", {
      value: year
    }, year)))), m("label", null, m("span", {
      key: "label"
    }, label('powertrain')), m("select", {
      className: "FormControl",
      name: "powertrain",
      key: `${group.market}:${group.model}`,
      disabled: !group.model,
      value: group.powertrain || '',
      onchange: e => group.powertrain = e.target.value
    }, m("option", {
      value: ""
    }, label('optional_unknown')), this.catalog.powertrains.filter(item => this.powertrains(group).includes(item.id)).map(item => m("option", {
      value: item.id
    }, item.name)))), conditions && ['version', 'test'].map(field => m("label", {
      className: field === 'test' ? 'BydTalkFullField' : ''
    }, m("span", null, label(field)), m("input", {
      className: "FormControl",
      disabled: !group.model,
      placeholder: label(field + '_example'),
      maxlength: field === 'version' ? 100 : 500,
      value: group[field] || '',
      oninput: e => group[field] = e.target.value
    })))), m("p", {
      className: "BydTalkFieldHelp"
    }, label('model_year_help')), !conditions && index > 0 && m(Button, {
      className: "Button Button--link BydTalkMakePrimary",
      icon: "fas fa-star",
      type: "button",
      onclick: () => {
        [this.groups[0], this.groups[index]] = [this.groups[index], this.groups[0]];
      }
    }, label('make_primary')))), m("p", {
      className: "BydTalkFieldHelp"
    }, label('catalog_help')), m("div", {
      className: "BydTalkVehicleFooter"
    }, m(Button, {
      className: "Button",
      icon: "fas fa-plus",
      type: "button",
      disabled: this.loading || this.groups.length >= 10,
      onclick: () => this.groups.push(this.newGroup())
    }, label('add_vehicle')), m("div", null, m(Button, {
      className: "Button Button--link",
      type: "button",
      disabled: this.loading,
      onclick: () => this.hide()
    }, label('cancel')), m(Button, {
      type: "submit",
      className: "Button Button--primary",
      loading: this.loading
    }, label(conditions ? 'apply_conditions' : 'save'))))));
  }
}
class ReportsModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.reports = [];
    this.loadReports();
  }
  className() {
    return 'BydTalkReportsModal';
  }
  async loadReports() {
    this.fetching = true;
    this.loadError = false;
    try {
      this.reports = await app.request({
        method: 'GET',
        url: endpoint('reports')
      });
    } catch {
      this.loadError = true;
    } finally {
      this.fetching = false;
      m.redraw();
    }
  }
  title() {
    return label('reports_title');
  }
  content() {
    return m("div", {
      className: "Modal-body"
    }, this.fetching ? m(LoadingIndicator, null) : this.loadError ? m("div", {
      role: "alert"
    }, m("p", null, label('load_error')), m(Button, {
      className: "Button",
      onclick: () => this.loadReports()
    }, label('try_again'))) : this.reports.length ? this.reports.map(r => m("article", {
      className: "BydTalkReport"
    }, m("a", {
      href: app.forum.attribute('baseUrl') + '/d/' + r.discussion_id
    }, label('discussion'), " ", r.discussion_id, " \xB7 ", r.language), m("p", null, r.reason), m("p", {
      className: "BydTalkReportStatus"
    }, r.generation === r.current_generation ? label('report_' + r.status) : label('expired')), m("details", null, m("summary", null, label('original_translation')), m("pre", null, r.source), m("pre", null, r.text)), r.generation === r.current_generation && r.status === 'pending' && m(Button, {
      className: "Button",
      loading: r.loading,
      onclick: async () => {
        r.loading = true;
        try {
          await app.request({
            method: 'POST',
            url: endpoint(`translations/${r.translation_id}/retry`)
          });
          r.status = 'handled';
        } catch {
          this.alertAttrs = {
            type: 'error',
            content: label('save_error')
          };
        } finally {
          r.loading = false;
          m.redraw();
        }
      }
    }, label('retry')))) : m("p", null, label('no_reports')));
  }
}
async function poll() {
  if (document.hidden || !visible.size || paused) return;
  if (polls >= 12 && ++polls % 6 !== 0) return;
  const pending = [...visible.values()].filter(model => model.attribute('btTranslation')?.status === 'pending');
  if (!pending.length) return;
  polls++;
  try {
    const response = await app.request({
      method: 'GET',
      url: endpoint('status'),
      params: {
        ids: pending.map(model => model.attribute('btTranslation').id).join(',')
      }
    });
    paused = response.paused;
    const posts = response.items.filter(row => row.post_id && row.status !== 'pending').map(row => row.post_id);
    if (posts.length) await app.store.find('posts', {
      filter: {
        id: posts.join(',')
      }
    });
    const discussions = [...new Set(response.items.filter(row => !row.post_id && row.status !== 'pending').map(row => row.discussion_id))];
    await Promise.all(discussions.map(id => app.store.find('discussions', id)));
    m.redraw();
  } catch {
    if (polls >= 3) paused = true;
  }
}
export default app.initializers.add('bydtalk-community', () => {
  app.btEditVehicles = () => showModal(VehiclesModal);
  app.btEditLanguages = () => showModal(LanguagesModal);
  app.beforeMount(() => {
    language = app.data.btLanguage || document.documentElement.lang?.split('-')[0] || 'en';
  });
  override(app, 'request', function (original, options) {
    return original({
      ...options,
      headers: {
        ...options.headers,
        'X-BYDTalk-Language': language
      }
    });
  });
  app.routes['bydtalk.localized'] = {
    ...app.routes.discussion,
    path: '/l/:language/d/:id'
  };
  app.routes['bydtalk.localized.near'] = {
    ...app.routes.discussion,
    path: '/l/:language/d/:id/:near'
  };
  override(app.route, 'discussion', function (original, discussion, near) {
    return app.route(near && near !== 1 ? 'bydtalk.localized.near' : 'bydtalk.localized', {
      language,
      id: discussion.slug(),
      near: near && near !== 1 ? near : undefined
    });
  });
  override(Discussion.prototype, 'title', function (original) {
    const t = this.attribute('btTranslation');
    return originals.has('d' + this.id()) ? t?.original || original() : t?.text || original();
  });
  extend('flarum/forum/components/RenameDiscussionModal', 'oninit', function () {
    const original = this.discussion.attribute('btTranslation')?.original;
    if (typeof original === 'string') {
      this.currentTitle = original;
      this.newTitle(original);
    }
  });
  override(Post.prototype, 'contentHtml', function (original) {
    return originals.has(this.id()) ? this.attribute('btTranslation')?.originalHtml || original() : original();
  });
  override(CommentPost.prototype, 'content', function (original) {
    const result = original();
    const comment = result.find(node => node?.tag === Comment);
    if (comment) {
      comment.attrs.btPost = this.attrs.post;
      comment.attrs.contentHtml = body(this.attrs.post);
    }
    return result;
  });
  override(Comment.prototype, 'view', function (original) {
    const result = original(),
      post = this.attrs.btPost,
      t = post?.attribute('btTranslation');
    if (!t || this.attrs.isHidden || this.attrs.isEditing) return result;
    result[1].attrs.lang = originals.has(post.id()) || t.status !== 'ready' ? originalLanguage(t) : language;
    result[1].attrs.dir = result[1].attrs.lang === 'ar' ? 'rtl' : 'auto';
    result.splice(1, 0, m(TextOptions, {
      translation: t,
      entryKey: post.id()
    }));
    const conditions = post.attribute('btConditions') || [];
    if (conditions.length) result.push(m("div", {
      className: "BydTalkConditions"
    }, conditions.map(group => m("div", null, groupText(group)))));
    return result;
  });
  extend(CommentPost.prototype, 'oninit', function () {
    this.subtree.check(() => originals.has(this.attrs.post.id()), () => JSON.stringify(this.attrs.post.attribute('btTranslation')));
  });
  extend(CommentPost.prototype, 'oncreate', function () {
    visible.set('p' + this.attrs.post.id(), this.attrs.post);
    polls = 0;
    paused = false;
  });
  extend(CommentPost.prototype, 'onremove', function () {
    visible.delete('p' + this.attrs.post.id());
  });
  extend(DiscussionHero.prototype, 'items', function (items) {
    const d = this.attrs.discussion,
      t = d.attribute('btTranslation');
    if (t) {
      const title = items.get('title');
      title.attrs.lang = originals.has('d' + d.id()) || t.status !== 'ready' ? originalLanguage(t) : language;
      title.attrs.dir = title.attrs.lang === 'ar' ? 'rtl' : 'auto';
      items.add('btTextOptions', m(TextOptions, {
        translation: t,
        entryKey: 'd' + d.id()
      }), -10);
    }
    const groups = d.attribute('btConditions') || [];
    if (groups.length) items.add('btConditions', m("div", null, groups.map(group => m("div", null, groupText(group)))), -20);
  });
  extend(DiscussionHero.prototype, 'oncreate', function () {
    visible.set('d' + this.attrs.discussion.id(), this.attrs.discussion);
    polls = 0;
    paused = false;
  });
  extend(DiscussionHero.prototype, 'onremove', function () {
    visible.delete('d' + this.attrs.discussion.id());
  });
  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    if (!this.attrs.params.q) return;
    const post = this.attrs.post || this.attrs.discussion.mostRelevantPost() || this.attrs.discussion.firstPost();
    const t = post?.attribute('btTranslation');
    if (t && t.status !== 'ready' && t.sourceLanguage !== language) items.remove('excerpt');
  });
  const topics = ['news', 'buying', 'charging', 'ownership', 'software', 'troubleshooting', 'road-trips', 'safety', 'community'];
  override(Tag.prototype, 'name', function (original) {
    return topics.includes(this.slug()) ? label('topic_' + this.slug().replaceAll('-', '_')) : original();
  });
  override(Tag.prototype, 'description', function (original) {
    return this.attribute('btDescription') ?? original();
  });
  extend(TextEditor.prototype, 'controlItems', function (items) {
    const submit = items.get('submit');
    if (submit) submit.attrs['aria-label'] = extractText(this.attrs.submitLabel);
  });
  // SearchModal is lazy-loaded and not exported by Flarum's compatibility API.
  extend(Modal.prototype, 'oncreate', function () {
    if (!this.$().hasClass('SearchModal')) return;
    this.$().on('click', 'a[href]', () => this.hide());
    const selectResult = this.selectResult.bind(this);
    this.selectResult = () => {
      const item = this.getItem(this.index);
      const navigate = this.query() && (item.attr('data-id') || item.find('a[href]').length);
      selectResult();
      if (navigate) this.hide();
    };
  });
  extend(HeaderSecondary.prototype, 'items', function (items) {
    items.remove('locale');
    items.add('btLanguage', m(Button, {
      className: "Button Button--link",
      onclick: () => showModal(LanguagesModal)
    }, names()[language] || language), 25);
  });
  extend(SessionDropdown.prototype, 'items', function (items) {
    items.add('btLanguage', m(Button, {
      icon: "fas fa-globe",
      onclick: () => showModal(LanguagesModal)
    }, label('reading_language')), 76);
    items.add('btVehicle', m(LinkButton, {
      icon: "fas fa-car",
      href: app.route('garage')
    }, label('my_garage')), 75);
    if (app.session.user?.isAdmin()) items.add('btReports', m(Button, {
      icon: "fas fa-comment-dots",
      onclick: () => showModal(ReportsModal)
    }, label('feedback')), 74);
  });
  extend('flarum/forum/components/SettingsPage', 'accountItems', items => {
    items.add('btLanguage', m(Button, {
      className: "Button",
      icon: "fas fa-globe",
      onclick: () => showModal(LanguagesModal)
    }, label('reading_language')), 80);
  });
  for (const Composer of ['flarum/forum/components/DiscussionComposer', 'flarum/forum/components/ReplyComposer']) {
    extend(Composer, 'headerItems', function (items) {
      items.add('btConditions', m(Button, {
        className: "Button Button--link",
        icon: "fas fa-car",
        onclick: () => showModal(VehiclesModal, {
          conditions: this.btConditions || [],
          onSave: groups => this.btConditions = groups
        })
      }, this.btConditions?.length ? label('conditions_count', {
        count: this.btConditions.length
      }) : label('conditions_title')), -10);
    });
    extend(Composer, 'data', function (data) {
      if (this.btConditions) data.btConditions = this.btConditions;
    });
  }
  extend(UserCard.prototype, 'infoItems', function (items) {
    const preferredLanguage = this.attrs.user.attribute('btPreferredLanguage');
    if (preferredLanguage && names()[preferredLanguage]) items.add('btPreferredLanguage', m("span", null, label('preferred_language', {
      language: m("bdi", {
        lang: preferredLanguage
      }, names()[preferredLanguage])
    })), 80);
    (this.attrs.user.attribute('btVehicles') || []).forEach((vehicle, i) => items.add('btVehicle' + i, m("span", {
      className: "BydTalkVehicleBadge"
    }, groupText(vehicle))));
  });
  setInterval(poll, 5000);
});
flarum.reg.add('bydtalk-community', 'forum', app);